import React, { useRef, useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { FaPlay, FaPause, FaVolumeMute, FaVolumeDown, FaVolumeUp } from 'react-icons/fa';
import { FaVolumeOff } from 'react-icons/fa6';
import { IoIosSkipBackward, IoIosSkipForward } from 'react-icons/io';
import { MdLanguage, MdOutlineShutterSpeed, MdFullscreen, MdOutlineFullscreenExit } from 'react-icons/md';

const VIDEO_SRC = 'https://firebasestorage.googleapis.com/v0/b/videa-c1b8f.appspot.com/o/Episode%20130%20-%20Dyer%20Island.mp4?alt=media&token=18ed2665-4644-4bb6-893c-cbb5443f155b';
const CZ_AUDIO = '/cz-audio';

export default function Player() {
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const audioRef = useRef(null);
    const seekRef = useRef(null);
    const volPopupRef = useRef(null);
    const volDraggingRef = useRef(false);
    // ensure video element fills viewport (cover) even if ReactPlayer injects it later
    useEffect(() => {
        function applyCover() {
            try {
                const internal = playerRef.current && playerRef.current.getInternalPlayer && playerRef.current.getInternalPlayer();
                if (internal && internal.style) {
                    internal.style.objectFit = 'cover';
                    internal.style.width = '100%';
                    internal.style.height = '100%';
                    internal.style.position = 'absolute';
                    internal.style.left = '0';
                    internal.style.top = '0';
                }
            } catch {}
        }
        applyCover();
        // keep trying for a short while in case element mounts later
        const t = setInterval(applyCover, 400);
        const stop = setTimeout(() => clearInterval(t), 3000);
        return () => { clearInterval(t); clearTimeout(stop); };
    }, []);

    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false); // video muted state when using external audio
    const [volume, setVolume] = useState(0.9); // 0..1
    const [played, setPlayed] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [duration, setDuration] = useState(0);
    const [controlsVisible, setControlsVisible] = useState(true);
    const [showVol, setShowVol] = useState(false);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [language, setLanguage] = useState('en'); // 'en' or 'cz'
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [isSeeking, setIsSeeking] = useState(false);

    // helper: počítá a nastaví volume podle události (mouse / touch) relativně k vol-pop výšce
    function computeAndSetVolumeFromEvent(e) {
        if (!volPopupRef.current) return;
        const rect = volPopupRef.current.getBoundingClientRect();
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const y = clientY - rect.top;
        const h = rect.height || 1;
        const pct = Math.max(0, Math.min(1, 1 - (y / h))); // bottom->top
        changeVolumePct(pct);
    }

    // global listeners for dragging volume (mouse + touch)
    useEffect(() => {
        function onMove(e) {
            if (!volDraggingRef.current) return;
            computeAndSetVolumeFromEvent(e);
            e.preventDefault && e.preventDefault();
        }
        function onUp() {
            if (!volDraggingRef.current) return;
            volDraggingRef.current = false;
            // re-allow text selection
            try { document.body.style.userSelect = ''; } catch {}
        }
        window.addEventListener('mousemove', onMove);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchend', onUp);
        };
    }, []);

    useEffect(() => {
        const prevBodyOverflow = document.body.style.overflow;
        const prevDocBg = document.documentElement.style.background;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.background = '#000';
        return () => {
            document.body.style.overflow = prevBodyOverflow;
            document.documentElement.style.background = prevDocBg;
        };
    }, []);

    useEffect(() => {
        let hideTimeout = null;
        const HIDE_DELAY = 5000; // milliseconds

        function scheduleHide() {
            clearTimeout(hideTimeout);
            hideTimeout = setTimeout(() => setControlsVisible(false), HIDE_DELAY);
        }

        function showControls() {
            setControlsVisible(true);
            clearTimeout(hideTimeout);
            // schedule hide after inactivity
            hideTimeout = setTimeout(() => setControlsVisible(false), HIDE_DELAY);
        }

        const onMouseMove = () => showControls();
        const onMouseLeave = () => scheduleHide();

        const c = containerRef.current;
        if (!c) return;
        c.addEventListener('mousemove', onMouseMove);
        c.addEventListener('mouseleave', onMouseLeave);
        // show initially
        showControls();

        return () => {
            c.removeEventListener('mousemove', onMouseMove);
            c.removeEventListener('mouseleave', onMouseLeave);
            clearTimeout(hideTimeout);
        };
    }, []);

    useEffect(() => {
        function onKey(e) {
            if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
            if (e.code === 'KeyF') toggleFullscreen();
            if (e.code === 'ArrowRight') skip(5);
            if (e.code === 'ArrowLeft') skip(-5);
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [duration, played, language]);

    // create audio element for external track
    useEffect(() => {
        const a = new Audio();
        a.crossOrigin = 'anonymous';
        a.preload = 'auto';
        a.volume = volume;
        a.playbackRate = playbackRate;
        // attach hidden audio element into DOM so browser treats it as present (helps play on user gesture)
        try {
            a.src = CZ_AUDIO;
            a.style.position = 'absolute';
            a.style.width = '1px';
            a.style.height = '1px';
            a.style.opacity = '0';
            a.style.pointerEvents = 'none';
            if (containerRef.current && containerRef.current.appendChild) containerRef.current.appendChild(a);
        } catch {}

        function onPlay() { setPlaying(true); }
        function onPause() { setPlaying(false); }
        function onTimeUpdate() {
            // when using external audio, drive played based on audio currentTime
            if (language === 'cz' && a.duration && duration) {
                const pct = Math.max(0, Math.min(1, a.currentTime / duration));
                setPlayed(pct);
            }
        }
        function onLoadedMeta() {
            // sometimes audio duration available; keep consistent with video duration if possible
            if (!duration && a.duration && !Number.isNaN(a.duration) && a.duration > 0) {
                setDuration(prev => prev || a.duration);
            }
        }

        a.addEventListener('play', onPlay);
        a.addEventListener('pause', onPause);
        a.addEventListener('timeupdate', onTimeUpdate);
        a.addEventListener('loadedmetadata', onLoadedMeta);

        audioRef.current = a;

        return () => {
            try {
                a.removeEventListener('play', onPlay);
                a.removeEventListener('pause', onPause);
                a.removeEventListener('timeupdate', onTimeUpdate);
                a.removeEventListener('loadedmetadata', onLoadedMeta);
                a.pause();
                if (containerRef.current && containerRef.current.contains(a)) containerRef.current.removeChild(a);
            } catch {}
            audioRef.current = null;
        };
    }, []);

    // sync playbackRate and volume to both players
    useEffect(() => {
        if (playerRef.current) {
            try {
                const internal = playerRef.current.getInternalPlayer && playerRef.current.getInternalPlayer();
                if (internal) internal.playbackRate = playbackRate;
            } catch {}
        }
        if (audioRef.current) audioRef.current.playbackRate = playbackRate;
    }, [playbackRate]);

    // poll internal video's buffered ranges so "loaded" (buffered) is visible immediately
    useEffect(() => {
        let t;
        function checkBuffered() {
            try {
                const internal = playerRef.current && playerRef.current.getInternalPlayer && playerRef.current.getInternalPlayer();
                if (internal && internal.buffered && internal.duration) {
                    const b = internal.buffered;
                    const end = b.length ? b.end(b.length - 1) : 0;
                    const pct = internal.duration ? Math.max(0, Math.min(1, end / internal.duration)) : 0;
                    setLoaded(prev => (pct > prev ? pct : prev));
                }
            } catch {}
        }
        checkBuffered();
        t = setInterval(checkBuffered, 400);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume]);

    // fullscreen state listener
    useEffect(() => {
        function onFS() { setIsFullscreen(Boolean(document.fullscreenElement)); }
        document.addEventListener('fullscreenchange', onFS);
        return () => document.removeEventListener('fullscreenchange', onFS);
    }, []);

    function togglePlay() {
        setPlaying(p => {
            const next = !p;
            // sync audio when using cz
            if (audioRef.current) {
                try {
                    if (language === 'cz') {
                        if (next) audioRef.current.play().catch(()=>{});
                        else audioRef.current.pause();
                    } else {
                        // ensure audio paused
                        audioRef.current.pause();
                    }
                } catch {}
            }
            return next;
        });
    }

    function handleProgress(state) {
        setPlayed(state.played);
        if (typeof state.loaded === 'number') setLoaded(state.loaded);
        const sec = state.playedSeconds ?? state.played * duration;
        // sync external audio time while playing cz
        if (language === 'cz' && audioRef.current && Math.abs((audioRef.current.currentTime || 0) - sec) > 0.5) {
            try { audioRef.current.currentTime = sec; } catch {}
        }
    }

    function handleDuration(d) { setDuration(d); }

    // enable dragging the red thumb by holding mouse anywhere on seekRef
    useEffect(() => {
        function onMove(e) {
            if (!isSeeking) return;
            if (!seekRef.current) return;
            const rect = seekRef.current.getBoundingClientRect();
            const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
            const pct = rect.width ? x / rect.width : 0;
            const seconds = pct * duration;
            setPlayed(pct);
            if (playerRef.current) playerRef.current.seekTo(seconds, 'seconds');
            if (audioRef.current && language === 'cz') {
                try { audioRef.current.currentTime = seconds; } catch {}
            }
        }
        function onUp() {
            if (!isSeeking) return;
            setIsSeeking(false);
            // ensure audio plays while seeking ended
            if (audioRef.current && language === 'cz') {
                try { audioRef.current.play().catch(()=>{}); } catch {}
            }
        }
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [isSeeking, duration, language]);

    function handleSeekPct(e) {
        const pct = Number(e.target.value) / 100;
        setPlayed(pct);
        const seconds = pct * duration;
        if (playerRef.current) playerRef.current.seekTo(seconds, 'seconds');
        if (audioRef.current && language === 'cz') {
            try { audioRef.current.currentTime = seconds; } catch {}
        }
    }

    function skip(sec) {
        if (!duration) return;
        const target = Math.max(0, Math.min(duration, duration * played + sec));
        if (playerRef.current) playerRef.current.seekTo(target, 'seconds');
        if (audioRef.current && language === 'cz') {
            try { audioRef.current.currentTime = target; } catch {}
        }
        setPlayed(target / duration);
        setPlaying(true);
        if (audioRef.current && language === 'cz') audioRef.current.play().catch(()=>{});
    }

    function toggleMute() {
        // muting means video muted and audio muted (if playing external)
        const next = !muted;
        setMuted(next);
        if (playerRef.current) {
            // react-player accepts playing props; internal video element can be muted via config attributes as well
            // we'll control via playerRef.getInternalPlayer().muted if available
            try { playerRef.current.getInternalPlayer().muted = next; } catch {}
        }
        if (audioRef.current) audioRef.current.muted = next;
    }

    function changeVolumePct(v) {
        const vv = Math.max(0, Math.min(1, v));
        setVolume(vv);
        setMuted(vv === 0);
    }

    function toggleFullscreen() {
        const container = containerRef.current;
        if (!container) return;
        if (document.fullscreenElement) document.exitFullscreen();
        else container.requestFullscreen();
    }

    function selectLanguage(lang) {
        setLanguage(lang);
        setLanguageMenuOpen(false);
        if (lang === 'cz') {
            // mute video and play external audio synced
            try {
                if (playerRef.current) playerRef.current.getInternalPlayer().muted = true;
            } catch {}
            setMuted(false); // keep external audio audible
            if (audioRef.current) {
                // set time to current video time and force-play the external audio
                let t = 0;
                try { t = (playerRef.current && playerRef.current.getCurrentTime && playerRef.current.getCurrentTime()) || (duration * played) || 0; } catch {}
                try {
                    // ensure audio element loads the source before setting time/playing
                    audioRef.current.load && audioRef.current.load();
                    audioRef.current.currentTime = t;
                    audioRef.current.volume = volume;
                    audioRef.current.muted = false;
                    const p = audioRef.current.play();
                    if (p && p.then) {
                        p.then(() => setPlaying(true)).catch(() => setPlaying(true));
                    } else {
                        setPlaying(true);
                    }
                } catch {}
            }
        } else {
            // back to video audio
            if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
            try { if (playerRef.current) playerRef.current.getInternalPlayer().muted = false; } catch {}
        }
    }

    const playedSeconds = duration * played;

    function formatTime(secFloat) {
        if (!secFloat || Number.isNaN(secFloat)) return '00:00';
        const sec = Math.floor(secFloat);
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    // choose volume icon
    function VolumeIcon() {
        if (muted || volume === 0) return <FaVolumeOff size={18} />;
        if (volume <= 0.3) return <FaVolumeMute size={18} />;
        if (volume <= 0.7) return <FaVolumeDown size={18} />;
        return <FaVolumeUp size={18} />;
    }

    return (
        <div ref={containerRef} style={{
                position: 'fixed',
                left: 0, top: 0, right: 0, bottom: 0,
                width: '100vw', height: '100vh',
                margin: 0, padding: 0,
                zIndex: 2147483647,
                background: '#000',
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center'
             }}>
            <style>{`
                html,body,#root{ margin:0; height:100%; overflow:hidden; } /* remove page scrollbar */
                .react-player-box { width:100vw; height:100vh; overflow:hidden; background:transparent; display:flex; flex-direction:column; color:#fff; font-family:Inter, Roboto, Arial, sans-serif; }
                .player-area { position:relative; flex:1 1 auto; background:transparent; }
                .player-inner { position:absolute; inset:0; }
                .controls { position: absolute; left:0; right:0; bottom:0; display:flex; align-items:center; gap:12px; padding:12px 18px; background: linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.55)); transition: opacity .18s, transform .18s; overflow: visible; }
                .controls.hidden { opacity:0; transform: translateY(10px); pointer-events:none; }
                .left-play { position:absolute; left:14px; bottom:18px; z-index:10; width:56px; height:56px; border-radius:8px; display:flex; align-items:center; justify-content:center; background: rgba(0,0,0,0.45); cursor:pointer; }
                .left-play-inline { width:56px; height:56px; border-radius:8px; display:flex; align-items:center; justify-content:center; background: rgba(0,0,0,0.45); cursor:pointer; }
                .icon-btn { background:transparent; border:none; color:#fff; width:44px; height:44px; display:flex; align-items:center; justify-content:center; cursor:pointer; border-radius:8px; }
                .icon-btn:hover { background: rgba(255, 255, 255, 0.14); }
                .seek-wrap { position:relative; flex:1; height:28px; display:flex; align-items:center; }
                /* progress visuals: rounded and under the input (input is on top and clickable) */
                .progress-red { position:absolute; top:50%; transform:translateY(-50%); height:6px; border-radius:999px; background:#E50914; pointer-events:none; z-index:1; }
                /* loaded/buffered bar: visible from start when loaded portion exists, lighter grey */
                .buffered { position:absolute; left:0; top:50%; transform:translateY(-50%); height:6px; border-radius:999px; background: rgba(255,255,255,0.22); pointer-events:none; z-index:0; }
                 /* slider on top so clicks work; remove native outlines/white box */
                 .seek { -webkit-appearance:none; appearance:none; width:100%; background:transparent; height:6px; z-index:3; border:none; outline:none; box-shadow:none; cursor:pointer; padding:0; }
                 .seek:focus { outline:none; box-shadow:none; }
                 .seek::-webkit-slider-runnable-track { height:6px; background: rgba(255, 255, 255, 0.12); border-radius:999px; }
                 .seek::-moz-range-track { height:6px; background: transparent; border-radius:999px; }
                 .seek::-webkit-slider-thumb { -webkit-appearance:none; width:14px; height:14px; border-radius:50%; background:#E50914; box-shadow:0 2px 4px rgba(0,0,0,0.6); margin-top:-4px; }
                 .seek::-moz-range-thumb { width:14px; height:14px; border-radius:50%; background:#E50914; border:0; }
                 /* unified volume popup + vertical slider */
                .vol-pop {
                  position: absolute;
                  bottom: 42px;
                  left: 8px;
                  width: 56px;
                  padding: 8px;
                  background: rgba(0,0,0,0.36);
                  border-radius: 12px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  z-index: 30;
                  pointer-events: auto;
                  user-select: none;
                }

                .vol-slider {
                  -webkit-appearance: none;
                  appearance: none;
                  writing-mode: bt-lr; /* bottom-to-top for WebKit/Chrome */
                  width: 12px;
                  height: 120px;
                  background: transparent;
                  border-radius: 8px;
                  padding: 0;
                  margin: 0;
                  display: block;
                }
                .vol-slider::-webkit-slider-runnable-track {
                  height:100%;
                  background: rgba(255,255,255,0.06);
                  border-radius:8px;
                }
                .vol-slider::-moz-range-track {
                  height:100%;
                  background: rgba(255,255,255,0.06);
                  border-radius:8px;
                }

                /* simple centered thumb without extra transforms that break visual movement */
                .vol-slider::-webkit-slider-thumb{
                  -webkit-appearance:none;
                  width:16px;
                  height:16px;
                  border-radius:50%;
                  background:#E50914;
                  box-shadow:0 2px 6px rgba(0,0,0,0.55);
                  border:2px solid rgba(255,255,255,0.06);
                  margin-top: -8px;
                }
                .vol-slider::-moz-range-thumb{
                  width:16px;
                  height:16px;
                  border-radius:50%;
                  background:#E50914;
                  border:2px solid rgba(255,255,255,0.06);
                  box-shadow:0 2px 6px rgba(0,0,0,0.55);
                }

                .time { color:#fff; font-family:monospace; font-size:13px; min-width:72px; text-align:right; opacity:0.95; }
                .title-ep { color:#fff; opacity:0.95; margin-left:12px; }
                /* vol-pop: grey rounded container, closer above icon so it doesn't hide while moving to it */
                .vol-pop { position:absolute; bottom:42px; left:4px; width:36px; height:auto; display:flex; align-items:center; justify-content:center; background: rgba(0,0,0,0.36); border-radius:14px; padding:6px; z-index:30; pointer-events:auto; }
                 /* vertical slider: transparent track, we paint fill with inline background gradient */
                 .vol-slider { -webkit-appearance:none; appearance:none; width:8px; height:112px; background:transparent; border-radius:8px; padding:0; margin:0; }
                 .vol-slider::-webkit-slider-runnable-track { height:100%; background: transparent; border-radius:8px; }
                 .vol-slider::-moz-range-track { height:100%; background: transparent; border-radius:8px; }
                 /* bigger red thumb */
                 .vol-slider::-webkit-slider-thumb { -webkit-appearance:none; width:14px; height:14px; border-radius:50%; background:#E50914; box-shadow:0 2px 6px rgba(0,0,0,0.55); border:2px solid rgba(255,255,255,0.06); margin-top:0px; margin-left: 5.5px;}
                 .vol-slider::-moz-range-thumb { width:16px; height:16px; border-radius:50%; background:#E50914; border:2px solid rgba(255,255,255,0.06); box-shadow:0 2px 6px rgba(0,0,0,0.55); }
                 /* popups appear above controls and do not affect layout */
                 .lang-menu {
                   position: absolute;
                   right: 0;
                   bottom: calc(100% + 10px);
                   background: rgba(0,0,0,0.85);
                   color: #fff;
                   padding: 8px;
                   border-radius: 6px;
                   min-width: 140px;
                   z-index: 99999;
                   pointer-events: auto;
                   box-shadow: 0 6px 20px rgba(0,0,0,0.6);
                 }
                 .menu-item { padding:8px; cursor:pointer; border-radius:4px; }
                 .menu-item:hover { background: rgba(255,255,255,0.04); }
                 .speed-menu {
                   position: absolute;
                   right: 0;
                   bottom: calc(100% + 10px);
                   background: rgba(0,0,0,0.85);
                   color: #fff;
                   padding: 8px;
                   border-radius: 6px;
                   z-index: 99999;
                   pointer-events: auto;
                   box-shadow: 0 6px 20px rgba(0,0,0,0.6);
                   display:flex;
                   gap:6px;
                 }
             `}</style>

            <div className="react-player-box" role="region" aria-label="Video player">
                <div className="player-area" onDoubleClick={toggleFullscreen}>
                    <div className="player-inner">
                        <ReactPlayer
                            ref={playerRef}
                            url={VIDEO_SRC}
                            playing={playing}
                            muted={language === 'cz' ? true : muted}
                            volume={volume}
                            playbackRate={playbackRate}
                            width="100%"
                            height="100%"
                            onProgress={handleProgress}
                            onDuration={handleDuration}
                            config={{ file: { attributes: { playsInline: true } } }}
                        />

                        {/* left big play/pause moved into controls (rendered before skip buttons) */}

                        {/* transparent controls bar bottom */}
                        <div className={`controls ${controlsVisible ? '' : 'hidden'}`} style={{ alignItems:'center' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                {/* Play/Pause placed left of skip buttons as requested */}
                                <button className="icon-btn left-play-inline" onClick={togglePlay} aria-label="Play/Pause" title="Play/Pause">
                                    {playing ? <FaPause size={20} /> : <FaPlay size={20} />}
                                </button>

                                <button className="icon-btn" onClick={() => skip(-5)} title="Back 5s" aria-label="Back 5s">
                                    <IoIosSkipBackward size={20} />
                                </button>

                                <button className="icon-btn" onClick={() => skip(5)} title="Forward 5s" aria-label="Forward 5s">
                                    <IoIosSkipForward size={20} />
                                </button>

                                <div style={{ position:'relative' }}>
                                    <button
                                        className="icon-btn"
                                        onMouseEnter={() => setShowVol(true)}
                                        onMouseLeave={() => setShowVol(false)}
                                        onClick={() => { setMuted(m => { const n = !m; if (audioRef.current) audioRef.current.muted = n; try { playerRef.current.getInternalPlayer().muted = n; } catch{} return n; }); }}
                                        aria-label="Volume"
                                        title="Volume"
                                    >
                                        <VolumeIcon />
                                    </button>

                                    {showVol && (
                                        <div
                                            className="vol-pop"
                                            ref={volPopupRef}
                                            onMouseEnter={() => setShowVol(true)}
                                            onMouseLeave={() => setShowVol(false)}
                                            onMouseDown={(e) => {
                                                // allow dragging when pressing anywhere inside popup
                                                volDraggingRef.current = true;
                                                try { document.body.style.userSelect = 'none'; } catch {}
                                                computeAndSetVolumeFromEvent(e);
                                                e.preventDefault && e.preventDefault();
                                            }}
                                            onTouchStart={(e) => {
                                                volDraggingRef.current = true;
                                                try { document.body.style.userSelect = 'none'; } catch {}
                                                computeAndSetVolumeFromEvent(e);
                                            }}
                                        >
                                            <input
                                                className="vol-slider"
                                                type="range"
                                                min={0}
                                                max={1}
                                                step={0.01}
                                                value={volume}
                                                onChange={(e) => changeVolumePct(Number(e.target.value))}
                                                onInput={(e) => changeVolumePct(Number(e.target.value))}
                                                style={{
                                                    background: `linear-gradient(to top, #E50914 ${Math.round(volume*100)}%, rgba(255,255,255,0.12) ${Math.round(volume*100)}%)`
                                                }}
                                            />
                                        </div>
                                     )}
                                </div>
                            </div>

                            <div
                                className="seek-wrap"
                                ref={seekRef}
                                style={{ margin:'0 12px', cursor:'pointer' }}
                                onMouseDown={(e) => {
                                    setIsSeeking(true);
                                    // immediate move on mousedown
                                    const rect = (seekRef.current && seekRef.current.getBoundingClientRect && seekRef.current.getBoundingClientRect());
                                    if (!rect) return;
                                    const x = e.clientX - rect.left;
                                    const pct = Math.max(0, Math.min(1, x / rect.width));
                                    const seconds = pct * duration;
                                    setPlayed(pct);
                                    if (playerRef.current) playerRef.current.seekTo(seconds, 'seconds');
                                    if (audioRef.current && language === 'cz') {
                                        try { audioRef.current.currentTime = seconds; } catch {}
                                        try { audioRef.current.play().catch(()=>{}); } catch {}
                                    }
                                }}
                            >
                                <div className="buffered" style={{ width: `${loaded * 100}%` }} />
                                <div className="progress-red" style={{ width: `${played * 100}%` }} />
                                <input
                                    className="seek"
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    value={played * 100}
                                    onChange={handleSeekPct}
                                    onMouseDown={() => setIsSeeking(true)}
                                    onMouseUp={() => setIsSeeking(false)}
                                    aria-label="Seek"
                                />
                            </div>

                            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                                <div className="title-ep">LEGO Ninjago: Svrchovaná Říše - Dyerův Ostrov</div>

                                <div className="time">{formatTime(playedSeconds)}</div>
                                <div className="time" style={{ minWidth:48, opacity:0.85 }}>{formatTime(duration)}</div>

                                {/* language */}
                                <div style={{ position:'relative' }}>
                                    <button className="icon-btn" onClick={() => setLanguageMenuOpen(s => !s)} title="Audio / Language">
                                        <MdLanguage size={18} />
                                    </button>
                                    {languageMenuOpen && (
                                        <div className="lang-menu" onMouseLeave={() => setLanguageMenuOpen(false)}>
                                            <div className="menu-item" onClick={() => selectLanguage('en')}>English (original)</div>
                                            <div className="menu-item" onClick={() => selectLanguage('cz')}>Čeština (dabing)</div>
                                        </div>
                                    )}
                                </div>

                                {/* speed */}
                                <div style={{ position:'relative' }}>
                                    <button className="icon-btn" title="Speed" onClick={() => setShowSpeedMenu(s => !s)}>
                                        <MdOutlineShutterSpeed size={18} />
                                    </button>
                                    {showSpeedMenu && (
                                        <div className="speed-menu" style={{ right:80 }}>
                                            <button className="menu-item" onClick={() => { setPlaybackRate(0.5); setShowSpeedMenu(false); }}>0.5x</button>
                                            <button className="menu-item" onClick={() => { setPlaybackRate(1); setShowSpeedMenu(false); }}>1x</button>
                                            <button className="menu-item" onClick={() => { setPlaybackRate(1.5); setShowSpeedMenu(false); }}>1.5x</button>
                                            <button className="menu-item" onClick={() => { setPlaybackRate(2); setShowSpeedMenu(false); }}>2x</button>
                                        </div>
                                    )}
                                </div>

                                <button className="icon-btn" onClick={toggleFullscreen} title="Fullscreen">
                                    {isFullscreen ? <MdOutlineFullscreenExit size={20} /> : <MdFullscreen size={20} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}